import React, { useEffect } from 'react';
import Image from '../AppImage';
import Icon from '../AppIcon';
import Button from './Button';

const VendorDetailModal = ({ vendor, isOpen, onClose }) => {
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    useEffect(() => {
        const handleEscape = (e) => {
            if (e?.key === 'Escape' && isOpen) {
                onClose();
            }
        };

        document.addEventListener('keydown', handleEscape);
        return () => document.removeEventListener('keydown', handleEscape);
    }, [isOpen, onClose]);

    const handleBackdropClick = (e) => {
        if (e?.target === e?.currentTarget) {
            onClose();
        }
    };

    const handleWhatsAppClick = () => {
        const message = encodeURIComponent(
            `Hi! I'm interested in your ${vendor?.category} services. I found you on ServiceHub.`
        );
        const whatsappUrl = `https://wa.me/${vendor?.phone}?text=${message}`;
        window.open(whatsappUrl, '_blank');
    };

    const handleCallClick = () => {
        window.location.href = `tel:${vendor?.phone}`;
    };

    if (!isOpen || !vendor) return null;

    return (
        <div
            className="modal-overlay"
            onClick={handleBackdropClick}
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
        >
            <div className="modal-backdrop" aria-hidden="true" />
            <div className="modal-content">
                <header className="modal-header">
                    <div className="modal-header-content">
                        <h2 id="modal-title" className="text-h4 font-heading">
                            Vendor Details
                        </h2>
                        <button
                            onClick={onClose}
                            className="modal-close-button"
                            aria-label="Close modal"
                        >
                            <Icon name="X" size={20} />
                        </button>
                    </div>
                </header>

                <div className="modal-body">
                    <Image
                        src={vendor?.image}
                        alt={`${vendor?.name} - ${vendor?.category}`}
                        className="w-full aspect-[16/9] object-cover rounded-xl mb-6"
                    />

                    <div className="space-y-6">
                        <div>
                            <div className="flex items-start justify-between gap-4 mb-2">
                                <h3 className="text-h3 font-heading">{vendor?.name}</h3>
                                <div className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-warning/10 text-warning">
                                    <Icon name="Star" size={16} />
                                    <span className="font-data font-medium">{vendor?.rating?.toFixed(1)}</span>
                                </div>
                            </div>
                            <p className="text-caption text-muted-foreground">{vendor?.category}</p>
                        </div>

                        <div>
                            <h4 className="text-h5 font-heading mb-2">About</h4>
                            <p className="text-body text-card-foreground leading-relaxed">
                                {vendor?.description}
                            </p>
                        </div>

                        <div>
                            <h4 className="text-h5 font-heading mb-3">Services Offered</h4>
                            <div className="flex flex-wrap gap-2">
                                {vendor?.services?.map((service, index) => (
                                    <span
                                        key={index}
                                        className="px-3 py-1.5 rounded-lg bg-muted text-caption font-medium"
                                    >
                                        {service}
                                    </span>
                                ))}
                            </div>
                        </div>

                        <div>
                            <h4 className="text-h5 font-heading mb-3">Location</h4>
                            <div className="flex items-start gap-3 p-4 rounded-lg bg-card border border-border">
                                <Icon name="MapPin" size={20} className="text-primary mt-0.5" />
                                <div>
                                    <p className="text-body font-medium">{vendor?.location}</p>
                                    <p className="text-caption text-muted-foreground mt-1">
                                        {vendor?.city}, {vendor?.state}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-h5 font-heading mb-3">Pricing</h4>
                            <div className="p-4 rounded-lg bg-primary/5 border border-primary/10">
                                <div className="flex items-baseline gap-2">
                                    <span className="text-h2 font-data text-primary">
                                        ₹{vendor?.price?.toLocaleString()}
                                    </span>
                                    <span className="text-caption text-muted-foreground">onwards</span>
                                </div>
                                <p className="text-caption text-muted-foreground mt-1">
                                    Final pricing depends on event requirements
                                </p>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-h5 font-heading mb-3">Contact Information</h4>
                            <div className="space-y-3">
                                <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-border">
                                    <Icon name="Phone" size={20} className="text-primary" />
                                    <span className="text-body font-data">{vendor?.phone}</span>
                                </div>
                                <div className="flex items-center gap-3 p-4 rounded-lg bg-card border border-border">
                                    <Icon name="Mail" size={20} className="text-primary" />
                                    <span className="text-body">{vendor?.email}</span>
                                </div>
                            </div>
                        </div>

                        <div>
                            <h4 className="text-h5 font-heading mb-3">Reviews</h4>
                            <div className="space-y-4">
                                {vendor?.reviews?.map((review, index) => (
                                    <div key={index} className="p-4 rounded-lg bg-card border border-border">
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="text-body font-medium">{review?.author}</span>
                                            <div className="flex items-center gap-1 text-warning">
                                                <Icon name="Star" size={14} />
                                                <span className="text-caption font-data">{review?.rating}</span>
                                            </div>
                                        </div>
                                        <p className="text-caption text-card-foreground">{review?.comment}</p>
                                        <p className="text-caption text-muted-foreground mt-2">{review?.date}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                <footer className="modal-footer">
                    <div className="modal-footer-content">
                        <div className="flex gap-3">
                            <Button
                                variant="outline"
                                iconName="Phone"
                                iconPosition="left"
                                onClick={handleCallClick}
                                className="flex-1"
                            >
                                Call Now
                            </Button>
                            <Button
                                variant="default"
                                iconName="MessageCircle"
                                iconPosition="left"
                                onClick={handleWhatsAppClick}
                                className="flex-1"
                            >
                                WhatsApp
                            </Button>
                        </div>
                    </div>
                </footer>
            </div>
        </div>
    );
};

export default VendorDetailModal;